/**
 * Карточка соты: данные с сервера GET /map/hex/:hexId (§Т2.2).
 */

import { formatCrystals, formatDistance, type LocaleCode } from "@hive/i18n";
import type { MapHexCard } from "@hive/shared/api/map";
import { isRitualTraceQuality, ritualManaCost, ritualStakeCrystals } from "@hive/shared";
import { useTranslation } from "react-i18next";

import { Crystals } from "./CrystalIcon.jsx";

import type { TraceView } from "../api/traces.js";

export interface HexCardProps {
  readonly locale: LocaleCode;
  readonly card: MapHexCard;
  readonly distanceFromHearth: number | null;
  readonly isHearth: boolean;
  readonly isHearthRing: boolean;
  readonly hasHearth: boolean;
  readonly traces: readonly TraceView[];
  readonly onClose: () => void;
  readonly onSetHearth: () => void;
  readonly onPerformRitual?: (traceId: number) => void;
  readonly onSimulateTrace?: () => void;
  readonly onCollectVault?: () => void;
  readonly onMoveHearth?: () => void;
  readonly hearthMoveMode?: boolean;
  readonly busy?: boolean;
}

export function HexCard({
  locale,
  card,
  distanceFromHearth,
  isHearth,
  isHearthRing,
  hasHearth,
  traces,
  onClose,
  onSetHearth,
  onPerformRitual,
  onSimulateTrace,
  onCollectVault,
  onMoveHearth,
  hearthMoveMode = false,
  busy = false,
}: HexCardProps): React.JSX.Element {
  const { t } = useTranslation(["hex", "common", "map", "economy", "hearth"]);

  const isOwnLand = card.vault !== undefined;

  const ownerLine = (): string => {
    if (isHearth) return t("map:hearth.current");
    if (isOwnLand) return t("hex:card.owner.you");
    if (!card.owner) return t("hex:card.owner.none");
    if (card.owner.kind === "clan") {
      return t("hex:card.owner.clan", { name: card.owner.name });
    }
    return t("hex:card.owner.player", { name: card.owner.name });
  };

  const isSanctuary = card.type === "sanctuary";
  const tributeFree = card.tribute.amount === 0;

  /**
   * Дань бывает нулевой по разным причинам, и объяснение должно совпадать с
   * причиной: «твой дом» на чужом конце города выглядело ошибкой расчёта.
   */
  const tributeFreeReason = (): string => {
    if (isHearth || isHearthRing) return t("hex:tribute.free.hearth");
    if (isOwnLand) return t("hex:tribute.free.own_land");
    if (isSanctuary) return t("hex:tribute.free.sanctuary");
    if (!card.owner) return t("hex:tribute.free.nobody");
    return t("hex:tribute.free.other");
  };
  const isEmpty = !card.owner && !isSanctuary;
  const ritualTrace = traces.find(
    (trace) =>
      trace.hexId === card.hexId &&
      isRitualTraceQuality(trace.quality as "echo" | "true" | "deep" | "anchor"),
  );
  const ritualStake = ritualStakeCrystals(isHearthRing);
  const ritualMana = ritualManaCost();

  const traceQualityLabel = (quality: string): string => {
    const key = `map:traces.quality.${quality}`;
    const label = t(key, { defaultValue: quality });
    return label;
  };

  const fogHint =
    card.fog === "dark"
      ? t("map:fog.hidden")
      : card.fog === "silhouette"
        ? t("map:fog.silhouette")
        : null;
  const detailsVisible = card.fog === "visible";
  /** Действия (Очаг, ритуал) доступны на силуэте — после тапа или GPS посещение уже записано. */
  const actionsVisible = card.fog !== "dark";

  if (card.fog === "dark") {
    return (
      <aside className="card" aria-label={t("hex:card.title")}>
        <header className="card__head">
          <div>
            <p className="card__warn">{fogHint}</p>
          </div>
          <button type="button" className="card__close" onClick={onClose}>
            {t("common:action.close")}
          </button>
        </header>
      </aside>
    );
  }

  return (
    <aside className="card" aria-label={t("hex:card.title")}>
      <header className="card__head">
        <div>
          <h2 className="card__kind">{t(`hex:kind.${card.type}`)}</h2>
          {detailsVisible && card.name ? <p className="card__name">{card.name}</p> : null}
          {detailsVisible ? <p className="card__owner">{ownerLine()}</p> : null}
          {!detailsVisible && fogHint ? <p className="card__note">{fogHint}</p> : null}
        </div>
        <button type="button" className="card__close" onClick={onClose}>
          {t("common:action.close")}
        </button>
      </header>

      <dl className="card__facts">
        {detailsVisible && distanceFromHearth !== null && (
          <div className="card__fact">
            <dt>{t("hex:card.label.distance_hearth")}</dt>
            <dd>{formatDistance(locale, distanceFromHearth)}</dd>
          </div>
        )}

        {detailsVisible && (
          <div className="card__fact">
            <dt>{t("hex:card.hearths", { count: card.hearths })}</dt>
          </div>
        )}

        {detailsVisible && !isSanctuary && card.vault !== undefined && (
          <div className="card__fact">
            <dt>
              <Crystals>
                {t("hex:card.vault", {
                  amount: formatCrystals(locale, card.vault * 1000, "hive"),
                })}
              </Crystals>
            </dt>
          </div>
        )}

        {detailsVisible && !isSanctuary && isOwnLand && card.vault !== undefined && card.vault > 0 && (
          <section className="card__section">
            <h3 className="card__section-title">{t("hex:card.section.vault")}</h3>
            <button
              type="button"
              className="button button--primary"
              disabled={busy}
              onClick={() => onCollectVault?.()}
            >
              {t("hex:card.collect")}
            </button>
          </section>
        )}

        {detailsVisible && hearthMoveMode && hasHearth && !isHearth && !isSanctuary && (
          <section className="card__section">
            <button
              type="button"
              className="button button--primary"
              disabled={busy}
              onClick={() => onMoveHearth?.()}
            >
              {t("hearth:move.confirm")}
            </button>
          </section>
        )}

        {detailsVisible && !isSanctuary && card.vaultHint && (
          <div className="card__fact">
            <dt>{t(`hex:card.vault.hint.${card.vaultHint}`)}</dt>
          </div>
        )}
      </dl>

      {detailsVisible && isSanctuary && <p className="card__note">{t("hex:card.sanctuary")}</p>}

      {detailsVisible && !isSanctuary && (
        <section className="card__section">
          <h3 className="card__section-title">{t("hex:card.section.tribute")}</h3>
          {isHearth || isHearthRing || tributeFree ? (
            <p className="card__free">{tributeFreeReason()}</p>
          ) : (
            <p className="card__note">
              <Crystals>
                {t("hex:tribute.preview.amount", {
                  amount: formatCrystals(locale, card.tribute.amount * 1000, "hive"),
                })}
              </Crystals>
            </p>
          )}
        </section>
      )}

      {detailsVisible && !isSanctuary && !isHearth && (
        <section className="card__section">
          <h3 className="card__section-title">{t("hex:card.section.siege")}</h3>
          <p className="card__note">
            <Crystals>
              {t("hex:siege.stake", {
                amount: formatCrystals(locale, card.siege.stake * 1000, "hive"),
              })}
            </Crystals>
          </p>
          {!card.siege.available && card.siege.reason && (
            <p className="card__warn">
              <Crystals>
                {t(`hex:siege.blocked.${card.siege.reason}`, {
                  amount: formatCrystals(locale, card.siege.stake * 1000, "hive"),
                  duration: "—",
                })}
              </Crystals>
            </p>
          )}
        </section>
      )}

      {actionsVisible && !hasHearth && !isSanctuary && (
        <section className="card__section">
          <button
            type="button"
            className="button button--primary"
            disabled={busy}
            onClick={onSetHearth}
          >
            {t("map:hearth.here")}
          </button>
          <p className="card__note">{t("map:hearth.set.hint")}</p>
        </section>
      )}

      {actionsVisible && isEmpty && hasHearth && (
        <section className="card__section">
          <h3 className="card__section-title">{t("hex:card.section.ritual")}</h3>
          {ritualStake === 0 ? (
            <p className="card__free">{t("hex:ritual.stake.free")}</p>
          ) : (
            <p className="card__note">
              <Crystals>
                {t("hex:ritual.stake", {
                  amount: formatCrystals(locale, ritualStake * 1000, "hive"),
                })}
              </Crystals>
            </p>
          )}
          <p className="card__note">{t("hex:ritual.mana", { amount: ritualMana })}</p>
          {ritualTrace ? (
            <>
              <p className="card__note">
                {t("hex:ritual.trace_ready", {
                  quality: traceQualityLabel(ritualTrace.quality),
                })}
              </p>
              <button
                type="button"
                className="button button--primary"
                disabled={busy}
                onClick={() => onPerformRitual?.(ritualTrace.id)}
              >
                {t("hex:ritual.perform")}
              </button>
            </>
          ) : (
            <p className="card__warn">{t("hex:ritual.no_trace")}</p>
          )}
          {onSimulateTrace && (
            <button
              type="button"
              className="button button--link card__debug"
              disabled={busy}
              onClick={onSimulateTrace}
            >
              {t("hex:ritual.simulate_trace")}
            </button>
          )}
        </section>
      )}
    </aside>
  );
}
